<!-- AddAgenciesToCompetition.vue -->
<template>
  <v-dialog
    v-model="dialog"
    max-width="700px"
    persistent
    overlay-opacity="0.85"
    content-class="modern-add-agencies-dialog"
    z-index="3000"
    @click:outside="handleClose"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header with gradient background -->
      <v-card-title
        class="editor-header d-flex justify-space-between align-center px-6 py-4"
      >
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-account-multiple-plus</v-icon>
          Add Agencies to Challenge
        </div>
        <v-btn
          icon
          @click="handleClose"
          class="close-btn"
          color="white"
          depressed
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help text -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          <span>Select agencies to add to <strong>{{ competition?.title }}</strong></span>
        </div>
      </v-card-text>

      <!-- Error/Success message -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message || errorMessage">
          <v-alert
            :type="messageType"
            class="mb-4 elevation-2"
            transition="scale-transition"
            :icon="
              messageType === 'success'
                ? 'mdi-check-circle'
                : 'mdi-alert-circle'
            "
          >
            {{ message || errorMessage }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- Main content -->
      <v-card-text class="px-6 py-2" style="max-height: 50vh; overflow-y: auto">
        <v-row>
          <v-col cols="12" class="py-2">
            <v-autocomplete
              v-model="selectedAgencies"
              :items="agencyList"
              item-text="name"
              item-value="_id"
              label="Select Agencies*"
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              prepend-inner-icon="mdi-office-building-marker"
              :rules="[rules.required]"
              :placeholder="
                agencyList.length
                  ? 'Search or select agencies...'
                  : 'No agencies available to add'
              "
              :search-input.sync="agencySearch"
              @change="updateAllSelected"
              class="modern-input agency-autocomplete"
              :filter="filterAgencies"
              return-object
              no-filter
              :loading="loading"
            >
              <template v-slot:prepend-item>
                <v-list-item
                  ripple
                  @click="selectAllAgencies"
                  v-if="agencyList.length > 0"
                >
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="allSelected"
                      color="primary"
                    ></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      <v-icon small class="mr-2">mdi-select-all</v-icon>
                      Select All Agencies
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider v-if="agencyList.length > 0" class="my-1"></v-divider>
              </template>

              <template v-slot:prepend-inner>
                <v-icon small color="primary" class="mr-2">mdi-office-building-marker</v-icon>
              </template>

              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-bind="attrs" v-on="on">
                  <v-list-item-avatar size="32" class="mr-3">
                    <v-img
                      :src="item.midThumbnail || require('@/assets/default.jpg')"
                      :alt="item.name"
                    ></v-img>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ item.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      <v-icon small class="mr-1">mdi-email</v-icon>
                      {{ item.email }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="isSelected(item)"
                      color="primary"
                      @click.stop
                    ></v-checkbox>
                  </v-list-item-action>
                </v-list-item>
              </template>

              <template v-slot:selection="{ attrs, item, parent, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  label
                  small
                  class="mr-2 agency-chip"
                  close
                  @click:close="parent.selectItem(item)"
                >
                  <v-avatar left size="18" class="mr-1">
                    <v-img
                      :src="item.midThumbnail || require('@/assets/default.jpg')"
                      :alt="item.name"
                    ></v-img>
                  </v-avatar>
                  <span class="font-weight-medium">{{ item.name }}</span>
                </v-chip>
              </template>

              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="text-center py-2">
                      <v-icon size="40" color="grey lighten-1" class="mb-2">
                        {{ loading ? 'mdi-loading' : 'mdi-account-search' }}
                      </v-icon>
                      <div class="text-subtitle-1 font-weight-medium">
                        {{ loading ? 'Loading agencies...' : 'No agencies found' }}
                      </div>
                      <div v-if="!loading" class="text-caption grey--text">
                        {{ agencySearch ? `No results for "${agencySearch}"` : 'No agencies available to add' }}
                      </div>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>

          <!-- Selected Count Summary -->
          <v-col cols="12" class="py-0" v-if="selectedAgencies.length > 0">
            <div class="d-flex align-center justify-space-between pa-3 rounded-lg" style="background: #f5f7fa;">
              <span class="text-body-2">
                <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                {{ selectedAgencies.length }} agency{{ selectedAgencies.length > 1 ? 'ies' : '' }} selected
              </span>
              <v-btn
                text
                small
                color="error"
                @click="clearSelection"
              >
                <v-icon small left>mdi-close-circle</v-icon>
                Clear All
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Action buttons -->
      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-space-between">
        <v-btn text @click="handleClose" color="grey darken-1" class="px-6" large :disabled="submitting">
          <v-icon left>mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="success"
          @click="addAgencies"
          depressed
          width="200"
          :loading="submitting"
          class="px-6 save-btn"
          large
          dark
          :disabled="selectedAgencies.length === 0"
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-account-plus</v-icon>
          Add {{ selectedAgencies.length }} Agency{{ selectedAgencies.length > 1 ? 'ies' : '' }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      timeout="4000"
      bottom
      right
      shaped
      class="snackbar"
    >
      <div class="snackbar-content">
        <v-icon size="20" class="mr-2">
          {{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
        </v-icon>
        <span>{{ snackbarMessage }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  name: 'AddAgenciesToCompetition',
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    onClose: {
      type: Function,
      required: true
    },
    competition: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      agencySearch: "",
      selectedAgencies: [],
      allAgencies: [],
      agencyList: [],
      allSelected: false,
      message: null,
      messageType: "error",
      errorMessage: null,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      rules: {
        required: value => (value && value.length > 0) || "Please select at least one agency"
      }
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    })
  },
  watch: {
    dialog: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadAgencies();
          this.selectedAgencies = [];
          this.agencySearch = '';
          this.allSelected = false;
          this.message = null;
          this.errorMessage = null;
        }
      }
    }
  },
  methods: {
    ...mapActions({
      getCompetition: "competition/getCompetition",
      addAgenciesToCompetition: "competition/addAgencyInCompetition"
    }),
    
    handleClose() {
      if (!this.submitting) {
        this.onClose();
      }
    },

    async loadAgencies() {
      try {
        this.loading = true;
        
        // Use the same getCompetition action with agencyId
        const agencyId = this.profile._id;
        const response = await this.getCompetition({ agencyId });
        
        // Get existing agency IDs from the competition
        const existingAgencyIds = new Set();
        if (this.competition?.requested_agency_ids) {
          this.competition.requested_agency_ids.forEach(req => {
            const id = req.user_id?._id || req.user_id || req;
            if (id) existingAgencyIds.add(id);
          });
        }
        
        // Filter out agencies already in the competition
        // Also filter out the current agency (creator)
        this.agencyList = response.filter(
          agency => 
            !existingAgencyIds.has(agency._id) && 
            agency._id !== this.profile._id
        );
        
        this.allSelected = false;
      } catch (error) {
        console.error('Failed to load agencies:', error);
        this.message = error.response?.data?.message || "Failed to load agencies";
        this.messageType = "error";
      } finally {
        this.loading = false;
      }
    },

    selectAllAgencies() {
      if (this.allSelected) {
        this.selectedAgencies = [];
      } else {
        this.selectedAgencies = [...this.agencyList];
      }
      this.allSelected = !this.allSelected; 
    },

    clearSelection() {
      this.selectedAgencies = [];
      this.allSelected = false;
    },

    // Update all selected state
    updateAllSelected() {
      const selectedIds = this.selectedAgencies.map(
        item => item._id || item
      );
      const allIds = this.agencyList.map(item => item._id);
      this.allSelected = allIds.every(id => selectedIds.includes(id));
    },

    // Filter agencies based on search
    filterAgencies(item, queryText) {
      if (!queryText) return true;
      
      const searchTerm = queryText.toLowerCase().trim();
      const name = (item.name || '').toLowerCase();
      const email = (item.email || '').toLowerCase();
      
      return name.includes(searchTerm) || email.includes(searchTerm);
    },

    // Check if an agency is selected
    isSelected(item) {
      return this.selectedAgencies.some(
        agency => agency._id === item._id || agency === item._id
      );
    },

    async addAgencies() {
      if (this.selectedAgencies.length === 0) {
        this.message = "Please select at least one agency";
        this.messageType = "error";
        return;
      }

      try {
        this.submitting = true;
        this.message = null;
        this.errorMessage = null;
        
        const agencyIds = this.selectedAgencies.map(a => a._id);
        
        // Call the add agencies action
        await this.addAgenciesToCompetition({
          competitionId: this.competition._id,
          agencyIds: agencyIds
        });
        
        this.message = `Successfully added ${this.selectedAgencies.length} agency${this.selectedAgencies.length > 1 ? 'ies' : ''} to the challenge`;
        this.messageType = "success";
        
        // Close after showing success
        setTimeout(() => {
          this.onClose();
        }, 1500);
      } catch (error) {
        console.error('Failed to add agencies:', error);
        this.message = error.response?.data?.message || error.message || "Failed to add agencies to challenge";
        this.messageType = "error";
        this.submitting = false;
      }
    },

    showMessage(message, color = 'success') {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  },
  mounted() {
    // Initial load if dialog is open
    if (this.dialog) {
      this.loadAgencies();
    }
  }
};
</script>

<style scoped>
.modern-add-agencies-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
  background: linear-gradient(135deg, #084468 0%, #1e658d 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

.modern-input::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  background-color: white !important;
}

.save-btn {
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.3) !important;
  transition: all 0.3s ease;
  border-radius: 20px !important;
  background: linear-gradient(135deg, #43a047, #2e7d32) !important;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(46, 125, 50, 0.4) !important;
}

.save-btn:active:not(:disabled) {
  transform: translateY(0);
}

.save-btn:disabled {
  opacity: 0.6;
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Agency Autocomplete Styles */
.agency-autocomplete {
  width: 100%;
}

.agency-autocomplete ::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  background-color: white !important;
  min-height: 56px !important;
}

.agency-autocomplete ::v-deep .v-select__selections {
  padding: 4px 0;
  flex-wrap: wrap;
  gap: 4px;
}

.agency-chip {
  background: linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%) !important;
  border: 1px solid rgba(58, 123, 213, 0.2) !important;
  border-radius: 20px !important;
  transition: all 0.2s ease;
}

.agency-chip:hover {
  background: linear-gradient(135deg, #e8eeff 0%, #dce6ff 100%) !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(58, 123, 213, 0.15);
}

.agency-chip .v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Dropdown item styles */
.v-list-item {
  transition: all 0.2s ease;
}

.v-list-item:hover {
  background: rgba(58, 123, 213, 0.05) !important;
}

.v-list-item-avatar {
  border: 2px solid rgba(58, 123, 213, 0.1);
  border-radius: 50% !important;
  overflow: hidden;
}

/* Scrollbar styling */
.modern-add-agencies-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-add-agencies-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-add-agencies-dialog::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #084468 0%, #1e658d 100%);
  border-radius: 10px;
}

.modern-add-agencies-dialog::-webkit-scrollbar-thumb:hover {
  background: #1e658d;
}

/* Scrollbar for dropdown */
.v-menu__content {
  max-height: 300px !important;
}

.v-menu__content::-webkit-scrollbar {
  width: 6px;
}

.v-menu__content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.v-menu__content::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #084468 0%, #1e658d 100%);
  border-radius: 10px;
}

.v-menu__content::-webkit-scrollbar-thumb:hover {
  background: #1e658d;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .agency-chip {
    font-size: 12px !important;
    padding: 2px 8px !important;
  }
  
  .agency-chip .v-avatar {
    width: 16px !important;
    height: 16px !important;
  }
  
  .v-list-item-avatar {
    width: 28px !important;
    height: 28px !important;
  }
  
  .save-btn {
    width: 100% !important;
  }
}

/* Snackbar */
.snackbar-content {
  display: flex;
  align-items: center;
}
</style>